(ns graph-ui.web.voice
  "THE VOICE SURFACE — speech to text for a prompt, text to speech for the
   entries the pane is reading (PLAN-ikc-v3-015-01 phase 6, the :voice kind in
   dsl/contexts/ui.edn v2.0-SNAPSHOT).

   WHAT IT WILL NOT DO. The recognized text lands in the prompt box named by the
   node's :target and stops there. Nothing is dispatched. The reader reads what
   the microphone heard, corrects it, and sends it — the platform's standing
   rule that user input travels verbatim and a person decides
   (ADR-ikc-v3-075-01). Speech recognition makes mistakes, so an automatic
   dispatch would send a command that nobody wrote.

   A HARNESS BLOCK IS NEVER SPOKEN. `<task-notification>` and its eight
   siblings are machine messages the harness writes with the role user. George
   found them in his own transcript and said 'I am not sure I wrote all this'.
   :conversation/items carries :harness? on every item for exactly this reason,
   and reading one aloud in a synthetic voice would repeat that defect in his
   ear instead of on his screen. THINKING IS NOT SPOKEN either, unless the node
   asks: the thinking is longer than the answer, and the answer is what a reader
   walking a dog wanted.

   IT DEGRADES OUT LOUD. The browser speech APIs need a secure context, and the
   deployment is plain HTTP until phase 5 lands on the server. So the control is
   PRESENT and DISABLED, with the reason and the remedy in words beside it, and
   the refusal is recorded on the observability surface. A silent no-op is what
   PRINCIPLE-ikc-v3-010-01 calls a defect, and a console line is a signal nobody
   walking a dog can read.

   THE TWO HALVES ARE ASKED SEPARATELY, on purpose. Recognition
   (`SpeechRecognition`) is secure-context-only in every browser that has it.
   Synthesis (`speechSynthesis`) usually is not. Reporting one verdict for both
   would tell the reader that reading aloud is impossible on the LAN when it
   works — a wrong diagnostic, which is worse than none
   (CLAUDE.md: correct the error message first)."
  (:require [reagent.core :as r]
            [clojure.string :as string]
            [graph-ui.web.activity :as activity]))

;; @feature F-ikc-v3-017-01-opencode-bridge
;; @adr ADR-ikc-v3-077-01-public-access-alatusresearch
;; @adr ADR-graph-ui-v0-013-01-one-layout-model
;; @concept CONCEPT-ikc-v3-003-01-observability-surface
;; CONCEPT: CONCEPT-ikc-v3-010-01-session-turn

;; ---------------------------------------------------------------------------
;; PURE: which entries are spoken, and what is said
;; ---------------------------------------------------------------------------

(def role-labels
  "What the loudspeaker says before an entry, per role. It never names the
   counterpart's kind: no consumer above the seam may ask which kind answered
   (ADR-ikc-v3-075-01 §2), and a voice is a consumer."
  {"user" "You said." "assistant" "Answer." "system" "System note."})

(defn entry-speech
  "One row as the sentence a loudspeaker says. Pure.

   `read-thinking?` appends the thinking after the answer, in that order,
   because a reader who asks for the thinking still wants the answer first.
   Returns nil for a row with nothing to say, so a caller never queues silence."
  [{:keys [role text thinking]} read-thinking?]
  (let [label (get role-labels (str role) (str (string/capitalize (str role)) "."))
        body  (string/trim (str (or text "")))
        think (string/trim (str (or thinking "")))
        parts (cond-> []
                (seq body)                     (conj body)
                (and read-thinking? (seq think)) (conj (str "Thinking. " think)))]
    (when (seq parts)
      (string/join " " (cons label parts)))))

(defn speakable
  "The rows the voice surface will speak, oldest first. Pure.

   THREE RULES, and they are the whole of the selection:
     1. A row flagged :harness? is dropped. Always, and whatever the reader
        asks for — this is not a preference, it is the harness fold
        (2026-08-20) applied to the ear.
     2. A row that would say nothing is dropped.
     3. `count` rows at most, taken from the NEWEST end, because the control is
        'read the newest entries aloud' and a conversation is thousands long."
  [rows opts]
  (let [read-thinking? (boolean (:read-thinking? opts))
        n-max          (or (:count opts) 3)
        said (->> rows
                  (remove :harness?)
                  (keep (fn [r] (when-let [s (entry-speech r read-thinking?)]
                                  {:entry-id (:entry-id r) :speech s})))
                  vec)
        n    (count said)]
    (if (and (pos-int? n-max) (> n n-max)) (subvec said (- n n-max)) said)))

;; ---------------------------------------------------------------------------
;; PURE: what this browser can actually do, and what to say when it cannot
;; ---------------------------------------------------------------------------

(defn dictate-status
  "Can this browser listen, and if not, what does the reader do about it? Pure —
   `facts` is {:secure? :recognition?}, read from the window by `browser-facts`.

   The two causes are reported apart because the remedies differ: an insecure
   context is fixed by the deployment, and a browser with no SpeechRecognition
   at all is fixed by another browser."
  [{:keys [secure? recognition?]}]
  (cond
    (not secure?)
    {:ok? false
     :reason (str "Dictation needs a secure context. This page is on plain HTTP, "
                  "so the browser withholds the microphone API. Open ikc3 over "
                  "HTTPS — that is phase 5 of the agent-bridge plan, applied on "
                  "the server by hand.")}

    (not recognition?)
    {:ok? false
     :reason (str "This browser has no SpeechRecognition. The page is on HTTPS, "
                  "so the context is not the problem — Chrome and Edge have it, "
                  "Firefox does not.")}

    :else {:ok? true :reason nil}))

(defn read-status
  "Can this browser speak, and if not, what does the reader do about it? Pure.
   `facts` is {:synthesis? :rows}. Having nothing to read is a REFUSAL with its
   own words, not a dead control: a reader who taps and hears nothing cannot
   tell an empty conversation from a broken button."
  [{:keys [synthesis? rows]}]
  (cond
    (not synthesis?)
    {:ok? false
     :reason (str "This browser has no speechSynthesis, so nothing can be read "
                  "aloud here.")}

    (zero? (or rows 0))
    {:ok? false
     :reason "Nothing to read — select a conversation with entries first."}

    :else {:ok? true :reason nil}))

;; ---------------------------------------------------------------------------
;; The window's own facts
;; ---------------------------------------------------------------------------

(defn recognition-ctor
  "The browser's SpeechRecognition constructor, under either of its two names,
   or nil. Read through aget because the prefixed name does not exist as a
   compile-time property in any externs file."
  []
  (when (exists? js/window)
    (or (aget js/window "SpeechRecognition")
        (aget js/window "webkitSpeechRecognition"))))

(defn browser-facts
  "What this window can do, as the map the pure status fns take."
  []
  {:secure?      (boolean (and (exists? js/window) (.-isSecureContext js/window)))
   :recognition? (boolean (recognition-ctor))
   :synthesis?   (boolean (and (exists? js/window) (.-speechSynthesis js/window)))})

;; ---------------------------------------------------------------------------
;; The prompt-box handshake
;; ---------------------------------------------------------------------------
;;
;; A :prompt-area owns its own edit buffer — a reagent atom inside its own
;; closure, which is right: the buffer is that component's state and nothing
;; else's. So the voice control cannot reach into it, and must not try. Instead
;; each prompt area REGISTERS its buffer here under its own :import-id, and the
;; voice control appends to the buffer it finds. The registry is keyed by the
;; authored id, not the widget id, so a re-seed does not break the pairing
;; (:import-id's own contract).
;;
;; A missing target is REPORTED, not ignored: a microphone that hears you and
;; drops the words is the silent contract this project treats as a defect.

(defonce ^:private prompt-buffers (r/atom {}))

(defn register-prompt!
  "A prompt area declares its buffer under `import-id`. Called from the widget's
   own ref, so it lasts exactly as long as the component does."
  [import-id buf]
  (when (and import-id buf) (swap! prompt-buffers assoc (str import-id) buf)))

(defn forget-prompt!
  "The prompt area is gone. Called from the same ref with a nil element."
  [import-id]
  (when import-id (swap! prompt-buffers dissoc (str import-id))))

(defn prompt-buffer
  "The buffer registered under `import-id`, or nil."
  [import-id]
  (get @prompt-buffers (str import-id)))

(defn append-text
  "`buffer` plus `heard`, with one space between them and no space at the front.
   Pure, because 'what did the dictation do to the buffer' is the part that can
   be wrong without being visible."
  [buffer heard]
  (let [b (str (or buffer "")) h (string/trim (str (or heard "")))]
    (cond
      (empty? h) b
      (empty? (string/trim b)) h
      :else (str b (when-not (string/ends-with? b " ") " ") h))))

(defn fill-prompt!
  "Append `heard` to the prompt area registered under `target`. Returns true
   when a buffer took it. NEVER dispatches."
  [target heard]
  (if-let [buf (prompt-buffer target)]
    (do (swap! buf append-text heard) true)
    false))

;; ---------------------------------------------------------------------------
;; The widget
;; ---------------------------------------------------------------------------

(defn- say!
  "Speak `queue` through the browser's synthesis, one utterance per entry, and
   call `done!` at the end. Cancels anything already speaking, so a second tap
   replaces the reading rather than talking over it."
  [queue language done!]
  (let [synth (.-speechSynthesis js/window)]
    (.cancel synth)
    (doseq [[i {:keys [speech]}] (map-indexed vector queue)]
      (let [u (js/SpeechSynthesisUtterance. speech)]
        (set! (.-lang u) (or language "en-US"))
        (when (= i (dec (count queue)))
          (set! (.-onend u) (fn [_] (done!))))
        (.speak synth u)))
    (when (empty? queue) (done!))))

(defn- listen!
  "Start recognition and hand each final transcript to `on-heard`. Returns the
   recognition object so the caller can stop it."
  [language on-heard on-end]
  (let [ctor (recognition-ctor)
        ;; js/Reflect.construct, not (new ctor): the constructor is a VALUE read
        ;; out of the window under one of two names, and `new` on a local is not
        ;; something the compiler can resolve to a type.
        rec  (js/Reflect.construct ctor #js [])]
    (set! (.-lang rec) (or language "en-US"))
    (set! (.-continuous rec) true)
    (set! (.-interimResults rec) false)
    (set! (.-onresult rec)
          (fn [ev]
            (let [results (.-results ev)]
              (doseq [i (range (.-resultIndex ev) (.-length results))]
                (let [r (aget results i)]
                  (when (.-isFinal r)
                    (on-heard (.-transcript (aget r 0)))))))))
    (set! (.-onerror rec) (fn [ev] (on-end (str (.-error ev)))))
    (set! (.-onend rec) (fn [_] (on-end nil)))
    (.start rec)
    rec))

(defn- refusal
  "The visible half of the transparent contract: the reason, on the surface, in
   the reader's own words, and one entry on the observability surface so the
   refusal is countable and not only readable."
  [what reason]
  [:div {:class "text-xs px-1 py-0.5 rounded"
         :style {:color "var(--warn)" :background "var(--panel)"}}
   [:span {:class "font-semibold"} (str "Voice — " what " unavailable: ")]
   reason])

(defn widget-voice
  "The :voice widget. Two controls at most, each present only when its own prop
   asks for it, each disabled with its reason when this browser cannot do it.

   The read control speaks the BOUND rows — the entries of the conversation this
   session reads, through :page-conversation-entries. It reads the newest
   :read-count of them, skips every harness block, and skips the thinking unless
   :read-thinking says otherwise."
  [_props _bus _session-id]
  (let [state (r/atom {:listening nil :speaking false :note nil})]
    (fn [{:keys [dictate read target language read-thinking read-count items
                 density]}
         _bus _session-id]
      (let [facts   (browser-facts)
            rows    (vec (or items []))
            queue   (speakable rows {:read-thinking? (boolean read-thinking)
                                     :count (or read-count 3)})
            dstat   (dictate-status facts)
            rstat   (read-status (assoc facts :rows (count queue)))
            {:keys [listening speaking note]} @state
            pad     (case density :compact "p-1" :roomy "p-3" "p-2")
            ;; --touch-target is the declared minimum a finger hits reliably.
            ;; Every control here is at least that big, which is the whole of
            ;; "touch targets are large enough" for this widget.
            btn     (fn [{:keys [label title on-click active? enabled?]}]
                      [:button
                       {:title    title
                        :disabled (not enabled?)
                        :class    (str "text-sm rounded-md border font-semibold "
                                       (if enabled? "cursor-pointer " "ikc-dim cursor-not-allowed "))
                        :style    (cond-> {:min-width "var(--touch-target)"
                                           :min-height "var(--touch-target)"
                                           :padding "0 0.75rem"
                                           :border-color "var(--border)"
                                           :color "var(--text)"
                                           :background "var(--field)"}
                                    active? (assoc :background "var(--accent-soft)"
                                                   :border-color "var(--accent)"))
                        :on-click (when enabled? on-click)}
                       label])
            stop-listen!
            (fn []
              (when-let [rec listening]
                (set! (.-onend rec) (fn [_] nil))
                (.stop rec))
              (swap! state assoc :listening nil))]
        (into
         [:div {:class (str "flex flex-col gap-1 " pad)}]
         (concat
          [(into [:div {:class "flex flex-row items-center gap-2 flex-wrap"}]
                 (concat
                  (when dictate
                    [(btn {:label    (if listening "◉ Listening — stop" "🎤 Speak")
                           :title    (or (:reason dstat)
                                         (str "Dictate into the prompt box \"" (str target)
                                              "\". The text lands there for you to send —"
                                              " nothing is sent by voice."))
                           :active?  (boolean listening)
                           :enabled? (:ok? dstat)
                           :on-click
                           (fn [_]
                             (if listening
                               (stop-listen!)
                               (let [rec (listen!
                                          language
                                          (fn [heard]
                                            (if (fill-prompt! target heard)
                                              (do (activity/record!
                                                   {:kind :ui :label "voice/heard"
                                                    :detail (str "-> " target " ("
                                                                 (count heard) " chars)")})
                                                  (swap! state assoc :note nil))
                                              (do (activity/record!
                                                   {:kind :ui :label "voice/no-target"
                                                    :detail (str target)})
                                                  (swap! state assoc :note
                                                         (str "Heard \"" heard "\" but no prompt box"
                                                              " named \"" (str target) "\" is on this"
                                                              " page, so the words went nowhere.")))))
                                          (fn [err]
                                            (swap! state assoc :listening nil)
                                            (when err
                                              (activity/record! {:kind :ui :label "voice/error"
                                                                 :detail err})
                                              (swap! state assoc :note
                                                     (str "The microphone stopped: " err)))))]
                                 (swap! state assoc :listening rec))))})])
                  (when read
                    [(btn {:label    (if speaking "■ Stop reading" "🔊 Read newest")
                           :title    (or (:reason rstat)
                                         (str "Read the newest " (count queue)
                                              " entries aloud. Harness notes are skipped"
                                              (if read-thinking
                                                ", thinking included."
                                                "; thinking is skipped.")))
                           :active?  speaking
                           :enabled? (or speaking (:ok? rstat))
                           :on-click
                           (fn [_]
                             (if speaking
                               (do (.cancel (.-speechSynthesis js/window))
                                   (swap! state assoc :speaking false))
                               (do (swap! state assoc :speaking true)
                                   (activity/record!
                                    {:kind :ui :label "voice/read"
                                     :detail (str (count queue) " of " (count rows)
                                                  " entries")})
                                   (say! queue language
                                         (fn [] (swap! state assoc :speaking false))))))})])
                  ;; The counts are on the surface, not only in the tooltip: a
                  ;; reader must be able to see that 4 of 9 entries were left
                  ;; out, and why, without hovering anything (Rule 2 of the
                  ;; inspection affordance — the FACT of hiding is visible).
                  (when (and read (seq rows))
                    [[:span {:class "text-xs" :style {:color "var(--faint)"}}
                      (let [skipped (- (count rows) (count queue))]
                        (str (count queue) " of " (count rows) " entries"
                             (when (pos? skipped)
                               (str " · " skipped " left out: harness notes"
                                    (when-not read-thinking ", thinking")
                                    ", older entries"))))]])))]
          (when (and dictate (not (:ok? dstat)))
            [(refusal "dictation" (:reason dstat))])
          (when (and read (not (:ok? rstat)) (not speaking))
            [(refusal "reading aloud" (:reason rstat))])
          (when note
            [[:div {:class "text-xs" :style {:color "var(--crit)"}} note]])))))))

;; ---------------------------------------------------------------------------
;; THE PHRASE TABLE — control needs no model
;; (ADR-graph-ui-v0-022-01, PLAN-ikc-v3-017-01 phase 8)
;;
;; George, 2026-08-21, asked for voice control that needs no model. So a spoken
;; command is a ROW in dsl/contexts/ui.edn :voice, matched exactly.
;;
;; EVERY FUNCTION HERE IS PURE AND TAKES THE ROWS AS AN ARGUMENT. Two reasons.
;; The table is a CONTEXT manifest and does not reach the browser yet — the
;; generated mirror carries the meta-ui vocabulary and not this level — so a
;; function that read a global would be untestable and unwired at once. And a
;; test that supplies its own rows measures the RULE rather than today's table.
;; ---------------------------------------------------------------------------

(defn normalise-heard
  "PURE: what a heard sentence looks like before it is matched. Lower case,
   trimmed, inner runs of space collapsed, and trailing sentence punctuation
   dropped.

   A RECOGNIZER PUNCTUATES. Firefox has none, but every other engine returns
   \"Four tiles.\" with a full stop, and an exact match against a table written
   without one would then never fire. This is the whole of the tolerance —
   there is no stemming and no distance measure."
  [heard]
  (-> (str heard)
      string/lower-case
      string/trim
      ;; INNER PUNCTUATION GOES TOO, not only trailing. A recognizer writes
      ;; "Computer, four tiles." and the comma sits between the prefix and the
      ;; command — so stripping only the end left "computer, four tiles", which
      ;; matched no prefix. Found by T-web-voice-024.
      (string/replace #"[.!?,;:]" "")
      (string/replace #"\s+" " ")
      string/trim))

(defn prefix-word
  "PURE: the word that opens a command when the microphone stays open, or nil."
  [rows]
  (some #(when (:prefix %) (normalise-heard (:phrase %))) rows))

(defn strip-prefix
  "PURE: `heard` with the prefix word removed, or nil when it is absent.

   HANDS-FREE NEEDS THIS (ADR decision 6). With the microphone open every heard
   sentence is a candidate, so a command must start with the declared word.
   Without it the words are dictation, or they are nothing — and nil is how this
   says which."
  [rows heard]
  (let [h (normalise-heard heard)
        p (prefix-word rows)]
    (when (and p (string/starts-with? h (str p " ")))
      (string/trim (subs h (count p))))))

(defn phrase-for
  "PURE: the row a heard sentence names, or nil.

   THE MATCH IS EXACT (ADR decision 4). No stemming, no distance measure, no
   nearest row. A sentence the table does not hold returns nil, and silence is
   the correct answer to words the machine does not know.

   :also IS MATCHED TOO, so one row may answer to several spellings — but each
   spelling still matches exactly, and validate-dsl refuses two rows that answer
   to one word.

   A CONFIRMATION ROW IS NOT A COMMAND and is never returned here: `yes` and
   `no` answer a question the machine asked, so they are read by
   `confirm-answer` instead. Returning one from this function would let a person
   say `yes` into a pane with nothing pending and have it mean something."
  [rows heard]
  (let [h (normalise-heard heard)]
    (when-not (string/blank? h)
      (first (for [row rows
                   :when (and (:command row)
                              (not (contains? row :confirm-answer))
                              (not (:prefix row)))
                   :let [words (into #{(normalise-heard (:phrase row))}
                                     (map normalise-heard (:also row)))]
                   :when (contains? words h)]
               row)))))

(defn confirm-answer
  "PURE: true, false, or nil for a sentence that is neither.

   NOTHING DESTRUCTIVE HAPPENS ON ONE HEARING (ADR decision 5). A row with
   :confirm true makes the machine ask aloud, and this reads the answer. nil
   means the person said something else, and a pending question stays pending —
   an unrecognised answer must never count as a yes."
  [rows heard]
  (let [h   (normalise-heard heard)
        ;; `some` WOULD WALK PAST A `no`. It stops at the first truthy value, and
        ;; a spoken no IS false — so "cancel" returned nil, which means
        ;; "unrecognised" and leaves a destructive question pending. Found by
        ;; T-web-voice-023. Find the ROW first, then read its answer.
        hit (first (for [row  rows
                         :when (contains? row :confirm-answer)
                         :let [words (into #{(normalise-heard (:phrase row))}
                                           (map normalise-heard (:also row)))]
                         :when (contains? words h)]
                     row))]
    (when hit (:confirm-answer hit))))
